import { useEffect, useState } from "react";
import api, { apiErr, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Users, CreditCard, Award, BookOpen, Trash2, Plus, Save, TrendingUp, Bell, Settings as SettingsIcon, KeyRound, Languages, Download, Eye } from "lucide-react";

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="bg-surface-card rounded-xl p-5 border border-white/10" style={{ backgroundColor: "#131C38" }}>
      <Icon className="h-5 w-5 text-gold mb-3" />
      <div className="font-display text-3xl font-extrabold text-white">{value}</div>
      <div className="text-xs text-slate-400 mt-1">{label}</div>
    </div>
  );
}

export default function Admin() {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [payments, setPayments] = useState([]);
  const [certs, setCerts] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [settings, setSettings] = useState({ notify_email: "", contact_email: "", contact_phone: "" });
  const [newUser, setNewUser] = useState({ name: "", email: "", password: "", role: "user" });
  const [issue, setIssue] = useState({ user_email: "", name: "", course_slug: "", final_score: "Manual", issue_date: "" });
  const [prices, setPrices] = useState({});
  const [addUserOpen, setAddUserOpen] = useState(false);
  const [issueCertOpen, setIssueCertOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewSlug, setPreviewSlug] = useState("");
  const [tempPwUser, setTempPwUser] = useState(null);
  const [tempPw, setTempPw] = useState("");
  const LANG_LABELS = { EN: "English", IT: "Italian", DE: "German", FR: "French", HU: "Hungarian", BG: "Bulgarian", RO: "Romanian", CS: "Czech", NL: "Dutch", ES: "Spanish", PL: "Polish", PT: "Portuguese" };

  const loadAll = () => {
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/users").then((r) => setUsers(r.data)).catch(() => {});
    api.get("/courses").then((r) => { setCourses(r.data); setPrices(Object.fromEntries(r.data.map((c) => [c.slug, c.price]))); }).catch(() => {});
    api.get("/admin/payments").then((r) => setPayments(r.data)).catch(() => {});
    api.get("/admin/certificates").then((r) => setCerts(r.data)).catch(() => {});
    api.get("/admin/schedules").then((r) => setSchedules(r.data)).catch(() => {});
    api.get("/admin/notifications").then((r) => setNotifications(r.data)).catch(() => {});
    api.get("/admin/settings").then((r) => setSettings(r.data)).catch(() => {});
  };
  useEffect(() => { loadAll(); }, []);

  const createUser = async () => {
    try { await api.post("/admin/users", newUser); toast.success("User created"); setNewUser({ name: "", email: "", password: "", role: "user" }); setAddUserOpen(false); loadAll(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const changeRole = async (id, role) => {
    try { await api.put(`/admin/users/${id}`, { role }); toast.success("Role updated"); loadAll(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const delUser = async (id, email) => {
    if (!window.confirm(`Delete user ${email}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/users/${id}`); toast.success("User deleted"); loadAll(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const setTempPassword = async () => {
    if (!tempPw || tempPw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    try {
      await api.post(`/admin/users/${tempPwUser.id}/temp-password`, { password: tempPw });
      toast.success("Temporary password set. Share it with the user — they must change it at first login.");
      setTempPwUser(null); setTempPw("");
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const savePrice = async (slug) => {
    try { await api.put(`/admin/courses/${slug}`, { price: Number(prices[slug]) }); toast.success("Price updated"); loadAll(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const issueCert = async () => {
    try { const { data } = await api.post("/admin/certificates/issue", issue); toast.success(`Certificate issued: ${data.number}`); setIssue({ user_email: "", name: "", course_slug: "", final_score: "Manual", issue_date: "" }); setIssueCertOpen(false); loadAll(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const revokeCert = async (num) => {
    if (!window.confirm(`Revoke certificate ${num}? This cannot be undone.`)) return;
    try { await api.delete(`/admin/certificates/${num}`); toast.success("Certificate revoked"); loadAll(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  const saveSettings = async () => {
    try { await api.put("/admin/settings", settings); toast.success("Settings saved"); loadAll(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-12" data-testid="admin-page">
      <p className="text-xs font-mono uppercase tracking-[0.2em] text-gold mb-1">Administration</p>
      <h1 className="font-display text-3xl lg:text-4xl font-extrabold text-white">Admin Panel</h1>

      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mt-8">
          <Stat icon={Users} label="Users" value={stats.users} />
          <Stat icon={CreditCard} label="Paid orders" value={stats.paid_orders} />
          <Stat icon={BookOpen} label="Enrollments" value={stats.enrollments} />
          <Stat icon={Award} label="Certificates" value={stats.certificates} />
          <Stat icon={TrendingUp} label="Revenue (EUR)" value={stats.revenue} />
        </div>
      )}

      <Tabs defaultValue="users" className="mt-10">
        <TabsList className="bg-surface border border-white/10 flex-wrap h-auto">
          <TabsTrigger value="users" data-testid="admin-tab-users">Users</TabsTrigger>
          <TabsTrigger value="courses" data-testid="admin-tab-courses">Courses & Prices</TabsTrigger>
          <TabsTrigger value="payments" data-testid="admin-tab-payments">Payments</TabsTrigger>
          <TabsTrigger value="certs" data-testid="admin-tab-certs">Certificates</TabsTrigger>
          <TabsTrigger value="schedules" data-testid="admin-tab-schedules">Schedules</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="admin-tab-notifications">Activity</TabsTrigger>
          <TabsTrigger value="settings" data-testid="admin-tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="users" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={addUserOpen} onOpenChange={setAddUserOpen}>
              <DialogTrigger asChild><Button className="btn-gold" data-testid="admin-add-user-btn"><Plus className="h-4 w-4 mr-1" />Add user</Button></DialogTrigger>
              <DialogContent className="bg-surface border-white/10 text-white">
                <DialogHeader><DialogTitle>New user</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>Name</Label><Input value={newUser.name} onChange={(e) => setNewUser({ ...newUser, name: e.target.value })} className="bg-surface-2 border-white/10 text-white" data-testid="admin-newuser-name" /></div>
                  <div><Label>Email</Label><Input type="email" value={newUser.email} onChange={(e) => setNewUser({ ...newUser, email: e.target.value })} className="bg-surface-2 border-white/10 text-white" data-testid="admin-newuser-email" /></div>
                  <div><Label>Password</Label><Input value={newUser.password} onChange={(e) => setNewUser({ ...newUser, password: e.target.value })} className="bg-surface-2 border-white/10 text-white" data-testid="admin-newuser-password" /></div>
                  <div><Label>Role</Label>
                    <Select value={newUser.role} onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger className="bg-surface-2 border-white/10 text-white"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-surface border-white/10 text-white"><SelectItem value="user">User</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                    </Select>
                  </div>
                </div>
                <DialogFooter><Button className="btn-gold" onClick={createUser} data-testid="admin-newuser-save">Create</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-surface rounded-xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead className="text-slate-400">Name</TableHead><TableHead className="text-slate-400">Email</TableHead><TableHead className="text-slate-400">Exam language</TableHead><TableHead className="text-slate-400">Role</TableHead><TableHead className="text-slate-400 text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {users.map((u) => (
                  <TableRow key={u.id} className="border-white/10" data-testid={`admin-user-${u.email}`}>
                    <TableCell className="text-white">{u.name}</TableCell>
                    <TableCell className="text-slate-300">{u.email}</TableCell>
                    <TableCell data-testid={`admin-user-lang-${u.email}`}>
                      {u.role === "admin"
                        ? <span className="text-slate-500">—</span>
                        : <span className="inline-flex items-center gap-1.5 text-slate-200"><Languages className="h-3.5 w-3.5 text-gold" />EN + {LANG_LABELS[u.exam_language] || u.exam_language || "EN"}</span>}
                    </TableCell>
                    <TableCell>
                      <Select value={u.role} onValueChange={(v) => changeRole(u.id, v)}>
                        <SelectTrigger className="w-32 bg-surface-2 border-white/10 text-white h-8"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-surface border-white/10 text-white"><SelectItem value="user">User</SelectItem><SelectItem value="admin">Admin</SelectItem></SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button size="sm" variant="ghost" className="text-gold hover:text-gold hover:bg-gold/10" onClick={() => { setTempPwUser(u); setTempPw(""); }} data-testid={`admin-temppw-user-${u.email}`} title="Set temporary password"><KeyRound className="h-4 w-4" /></Button>
                      <Button size="sm" variant="ghost" className="text-rose-400 hover:text-rose-300 hover:bg-rose-500/10" onClick={() => delUser(u.id, u.email)} data-testid={`admin-del-user-${u.email}`}><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Dialog open={!!tempPwUser} onOpenChange={(o) => { if (!o) { setTempPwUser(null); setTempPw(""); } }}>
            <DialogContent className="bg-surface border-white/10 text-white">
              <DialogHeader><DialogTitle>Set temporary password</DialogTitle></DialogHeader>
              <p className="text-sm text-slate-400">
                For <span className="text-white font-semibold">{tempPwUser?.email}</span>. Share this password with the user —
                they will be required to change it the first time they sign in.
              </p>
              <div><Label>Temporary password</Label><Input value={tempPw} onChange={(e) => setTempPw(e.target.value)} placeholder="At least 6 characters" className="bg-surface-2 border-white/10 text-white mt-1" data-testid="admin-temppw-input" /></div>
              <DialogFooter><Button className="btn-gold" onClick={setTempPassword} data-testid="admin-temppw-save"><KeyRound className="h-4 w-4 mr-2" />Set password</Button></DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        <TabsContent value="courses" className="mt-6">
          <div className="bg-surface rounded-xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead className="text-slate-400">Course</TableHead><TableHead className="text-slate-400">Provider</TableHead><TableHead className="text-slate-400">Price (EUR)</TableHead><TableHead className="text-slate-400 text-right">Save</TableHead></TableRow></TableHeader>
              <TableBody>
                {courses.map((c) => (
                  <TableRow key={c.slug} className="border-white/10" data-testid={`admin-course-${c.slug}`}>
                    <TableCell className="text-white">{c.title}</TableCell>
                    <TableCell className="text-slate-300">{c.provider}</TableCell>
                    <TableCell>
                      <Input type="number" value={prices[c.slug] ?? ""} onChange={(e) => setPrices({ ...prices, [c.slug]: e.target.value })}
                        className="w-28 bg-surface-2 border-white/10 text-white h-9" data-testid={`admin-price-${c.slug}`} />
                    </TableCell>
                    <TableCell className="text-right"><Button size="sm" className="btn-gold" onClick={() => savePrice(c.slug)} data-testid={`admin-save-price-${c.slug}`}><Save className="h-4 w-4" /></Button></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="payments" className="mt-6">
          <div className="bg-surface rounded-xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead className="text-slate-400">User</TableHead><TableHead className="text-slate-400">Course</TableHead><TableHead className="text-slate-400">Amount</TableHead><TableHead className="text-slate-400">Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {payments.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No payments yet.</TableCell></TableRow>}
                {payments.map((p) => (
                  <TableRow key={p.session_id} className="border-white/10">
                    <TableCell className="text-slate-300">{p.user_email}</TableCell>
                    <TableCell className="text-white">{p.course_title}</TableCell>
                    <TableCell className="text-slate-300">{p.amount} {p.currency}</TableCell>
                    <TableCell><span className={p.payment_status === "paid" ? "text-emerald-400" : "text-amber-400"}>{p.payment_status}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="certs" className="mt-6">
          <div className="flex justify-end mb-4">
            <Dialog open={issueCertOpen} onOpenChange={setIssueCertOpen}>
              <DialogTrigger asChild><Button className="btn-gold" data-testid="admin-issue-cert-btn"><Plus className="h-4 w-4 mr-1" />Issue certificate manually</Button></DialogTrigger>
              <DialogContent className="bg-surface border-white/10 text-white">
                <DialogHeader><DialogTitle>Issue certificate</DialogTitle></DialogHeader>
                <div className="space-y-3">
                  <div><Label>User email</Label><Input value={issue.user_email} onChange={(e) => setIssue({ ...issue, user_email: e.target.value })} className="bg-surface-2 border-white/10 text-white" data-testid="admin-issue-email" /></div>
                  <div><Label>Name on certificate</Label><Input value={issue.name} onChange={(e) => setIssue({ ...issue, name: e.target.value })} className="bg-surface-2 border-white/10 text-white" data-testid="admin-issue-name" /></div>
                  <div><Label>Course</Label>
                    <Select value={issue.course_slug} onValueChange={(v) => setIssue({ ...issue, course_slug: v })}>
                      <SelectTrigger className="bg-surface-2 border-white/10 text-white" data-testid="admin-issue-course"><SelectValue placeholder="Choose a course" /></SelectTrigger>
                      <SelectContent className="bg-surface border-white/10 text-white">{courses.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.title}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div><Label>Score</Label><Input value={issue.final_score} onChange={(e) => setIssue({ ...issue, final_score: e.target.value })} className="bg-surface-2 border-white/10 text-white" /></div>
                  <div><Label>Issue date <span className="text-slate-500 font-normal">(optional — defaults to today)</span></Label><Input type="date" value={issue.issue_date} onChange={(e) => setIssue({ ...issue, issue_date: e.target.value })} className="bg-surface-2 border-white/10 text-white" data-testid="admin-issue-date" /></div>
                </div>
                <DialogFooter><Button className="btn-gold" onClick={issueCert} data-testid="admin-issue-save">Issue</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
          <div className="bg-surface rounded-xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead className="text-slate-400">Code</TableHead><TableHead className="text-slate-400">Holder</TableHead><TableHead className="text-slate-400">Course</TableHead><TableHead className="text-slate-400">Score</TableHead><TableHead className="text-slate-400 text-right">Actions</TableHead></TableRow></TableHeader>
              <TableBody>
                {certs.length === 0 && <TableRow><TableCell colSpan={5} className="text-center text-slate-500 py-8">No certificates yet.</TableCell></TableRow>}
                {certs.map((c) => (
                  <TableRow key={c.number} className="border-white/10">
                    <TableCell className="font-mono text-gold">{c.number}</TableCell>
                    <TableCell className="text-white">{c.name}</TableCell>
                    <TableCell className="text-slate-300">{c.course_title}</TableCell>
                    <TableCell className="text-slate-300">{c.final_score}</TableCell>
                    <TableCell className="text-right">
                      <a href={`${API}/certificates/${encodeURIComponent(c.number)}/download?inline=1`} target="_blank" rel="noreferrer">
                        <Button size="sm" variant="ghost" className="text-electric hover:bg-electric/10" data-testid={`admin-view-cert-${c.number}`} title="View PDF"><Eye className="h-4 w-4" /></Button>
                      </a>
                      <a href={`${API}/certificates/${encodeURIComponent(c.number)}/download`}>
                        <Button size="sm" variant="ghost" className="text-gold hover:bg-gold/10" data-testid={`admin-download-cert-${c.number}`} title="Download PDF"><Download className="h-4 w-4" /></Button>
                      </a>
                      <Button size="sm" variant="ghost" className="text-rose-400 hover:bg-rose-500/10" onClick={() => revokeCert(c.number)} data-testid={`admin-revoke-${c.number}`} title="Revoke"><Trash2 className="h-4 w-4" /></Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="schedules" className="mt-6">
          <div className="bg-surface rounded-xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead className="text-slate-400">User</TableHead><TableHead className="text-slate-400">Course</TableHead><TableHead className="text-slate-400">Exam date</TableHead><TableHead className="text-slate-400">Status</TableHead></TableRow></TableHeader>
              <TableBody>
                {schedules.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-slate-500 py-8">No schedules yet.</TableCell></TableRow>}
                {schedules.map((s, i) => (
                  <TableRow key={i} className="border-white/10">
                    <TableCell className="text-slate-300">{s.user_email}</TableCell>
                    <TableCell className="text-white">{s.course_title}</TableCell>
                    <TableCell className="text-slate-300">{s.scheduled_at ? new Date(s.scheduled_at).toLocaleString("en-GB") : "-"}</TableCell>
                    <TableCell><span className="text-gold">{s.status}</span></TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="notifications" className="mt-6">
          <p className="text-sm text-slate-400 mb-4 flex items-center gap-2"><Bell className="h-4 w-4 text-gold" />Every key user action is logged here and emailed to the notification address configured in Settings.</p>
          <div className="bg-surface rounded-xl border border-white/10 overflow-hidden">
            <Table>
              <TableHeader><TableRow className="border-white/10 hover:bg-transparent"><TableHead className="text-slate-400">Event</TableHead><TableHead className="text-slate-400">Details</TableHead><TableHead className="text-slate-400">Time</TableHead></TableRow></TableHeader>
              <TableBody>
                {notifications.length === 0 && <TableRow><TableCell colSpan={3} className="text-center text-slate-500 py-8">No activity yet.</TableCell></TableRow>}
                {notifications.map((n, i) => (
                  <TableRow key={i} className="border-white/10">
                    <TableCell className="text-white font-medium">{n.event}</TableCell>
                    <TableCell className="text-slate-300 text-xs">{Object.entries(n.details || {}).map(([k, v]) => `${k}: ${v}`).join(" • ")}</TableCell>
                    <TableCell className="text-slate-400 text-xs">{n.created_at ? new Date(n.created_at).toLocaleString("en-GB") : "-"}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </TabsContent>

        <TabsContent value="settings" className="mt-6">
          <div className="max-w-xl bg-surface rounded-xl border border-white/10 p-6">
            <h3 className="font-display font-semibold text-white flex items-center gap-2 mb-1"><SettingsIcon className="h-5 w-5 text-gold" />Notifications & contact</h3>
            <p className="text-sm text-slate-400 mb-6">All user activity is emailed to the single notification address. The contact email & phone are shown only to signed-in members on the Contact page.</p>
            <div className="space-y-4">
              <div><Label className="text-slate-300">Notification email (receives all activity)</Label>
                <Input type="email" value={settings.notify_email || ""} onChange={(e) => setSettings({ ...settings, notify_email: e.target.value })} className="bg-surface-2 border-white/10 text-white mt-1" data-testid="settings-notify-email" /></div>
              <div><Label className="text-slate-300">Public contact email (members only)</Label>
                <Input type="email" value={settings.contact_email || ""} onChange={(e) => setSettings({ ...settings, contact_email: e.target.value })} className="bg-surface-2 border-white/10 text-white mt-1" data-testid="settings-contact-email" /></div>
              <div><Label className="text-slate-300">Public contact phone (members only)</Label>
                <Input value={settings.contact_phone || ""} onChange={(e) => setSettings({ ...settings, contact_phone: e.target.value })} className="bg-surface-2 border-white/10 text-white mt-1" data-testid="settings-contact-phone" /></div>
              <div><Label className="text-slate-300">Instructor / trainer name (on certificate)</Label>
                <Input value={settings.instructor_name || ""} onChange={(e) => setSettings({ ...settings, instructor_name: e.target.value })} className="bg-surface-2 border-white/10 text-white mt-1" data-testid="settings-instructor-name" /></div>
              <div><Label className="text-slate-300">Academy director name (on certificate)</Label>
                <Input value={settings.academy_director || ""} onChange={(e) => setSettings({ ...settings, academy_director: e.target.value })} className="bg-surface-2 border-white/10 text-white mt-1" data-testid="settings-academy-director" /></div>
              <Button className="btn-gold" onClick={saveSettings} data-testid="settings-save-btn"><Save className="h-4 w-4 mr-2" />Save settings</Button>
            </div>

            <div className="mt-8 pt-6 border-t border-white/10">
              <h3 className="font-display font-semibold text-white flex items-center gap-2 mb-1"><Award className="h-5 w-5 text-gold" />Certificate draft preview</h3>
              <p className="text-sm text-slate-400 mb-4">Render a sample certificate with the current instructor/director names and signatures to verify the design before going live.</p>
              <div className="flex flex-wrap items-end gap-3">
                <div className="flex-1 min-w-[220px]">
                  <Label className="text-slate-300">Course template</Label>
                  <Select value={previewSlug} onValueChange={setPreviewSlug}>
                    <SelectTrigger className="bg-surface-2 border-white/10 text-white mt-1" data-testid="cert-preview-course-select"><SelectValue placeholder="Select a course" /></SelectTrigger>
                    <SelectContent>
                      {courses.map((c) => <SelectItem key={c.slug} value={c.slug}>{c.title}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" className="border-white/20 text-white hover:bg-white/10 bg-transparent" onClick={() => setPreviewOpen(true)} data-testid="cert-preview-open-btn"><Award className="h-4 w-4 mr-2" />Preview certificate</Button>
              </div>
            </div>
          </div>

          <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
            <DialogContent className="max-w-4xl bg-surface border-white/10">
              <DialogHeader><DialogTitle className="text-white">Sample certificate — {courses.find((c) => c.slug === previewSlug)?.title || ""}</DialogTitle></DialogHeader>
              <iframe title="Certificate draft preview" src={`${API}/certificate-preview?slug=${encodeURIComponent(previewSlug || "")}&t=${previewOpen ? Date.now() : 0}`}
                className="w-full h-[70vh] rounded-lg bg-white" data-testid="cert-preview-frame" />
              <DialogFooter>
                <a href={`${API}/certificate-preview?slug=${encodeURIComponent(previewSlug || "")}`} target="_blank" rel="noreferrer">
                  <Button className="btn-gold" data-testid="cert-preview-download-btn"><Award className="h-4 w-4 mr-2" />Open in new tab</Button>
                </a>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>
      </Tabs>
    </div>
  );
}
